import React, { useState, useEffect } from 'react';
import axios from 'axios';

// API URL (configurable for development vs production)
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // In production, use relative path to same server
  : 'http://localhost:3001/api'; // In development, use localhost

const CoinGeckoMapper = ({ 
  contract, 
  setError,
  setSuccess,
  setLoading,
  loading 
}) => {
  // State for the token mappings
  const [tokenMappings, setTokenMappings] = useState({});
  const [availableCoins, setAvailableCoins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCoins, setFilteredCoins] = useState([]);
  const [newMapping, setNewMapping] = useState({ 
    address: '', 
    coinGeckoId: '',
    coinName: '' 
  });
  const [loadingCoins, setLoadingCoins] = useState(false);

  // Fetch the current mappings from API server
  useEffect(() => {
    const fetchMappings = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/mappings`);
        
        if (response.ok) {
          const data = await response.json();
          setTokenMappings(data);
          console.log(`Loaded ${Object.keys(data).length} token mappings from server`);
        } else {
          console.error("Failed to load mappings from server:", response.statusText);
          setTokenMappings({});
        }
      } catch (error) {
        console.error("Error fetching mappings:", error);
        setError(`Failed to load mappings: ${error.message}`);
        setTokenMappings({});
      } finally {
        setLoading(false);
      }
    };
    
    fetchMappings();
  }, [setLoading, setError]);

  // Save mappings to API server
  const saveMappings = async (mappings) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/mappings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mappings)
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      
      return true;
    } catch (error) {
      console.error("Error saving mappings:", error);
      setError(`Failed to save mappings: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch available coins from CoinGecko API
  const fetchCoinList = async () => {
    try {
      setLoadingCoins(true);
      setError('');
      const response = await axios.get('https://api.coingecko.com/api/v3/coins/list');
      if (response.data && Array.isArray(response.data)) {
        setAvailableCoins(response.data);
        setFilteredCoins(response.data.slice(0, 100)); // Show top 100 initially
        setSuccess('Successfully loaded CoinGecko coin list');
      }
    } catch (error) {
      console.error("Error fetching coin list:", error);
      setError(`Failed to fetch coin list: ${error.message}`);
    } finally {
      setLoadingCoins(false);
    }
  };

  // Filter coins based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCoins(availableCoins.slice(0, 100)); // Show top 100 when search is empty
    } else {
      const filtered = availableCoins.filter(coin => 
        coin.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        coin.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coin.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCoins(filtered.slice(0, 100)); // Limit to top 100 results
    }
  }, [searchTerm, availableCoins]);

  // Add a new mapping
  const addMapping = async () => {
    if (!newMapping.address || !newMapping.coinGeckoId) {
      setError('Both wallet address and CoinGecko ID are required');
      return;
    }

    if (!newMapping.address.startsWith('0x') || newMapping.address.length !== 42) {
      setError('Invalid wallet address format');
      return;
    }

    const normalizedAddress = newMapping.address.toLowerCase();
    const updatedMappings = {
      ...tokenMappings,
      [normalizedAddress]: { 
        id: newMapping.coinGeckoId, 
        name: newMapping.coinName || newMapping.coinGeckoId 
      }
    };

    const success = await saveMappings(updatedMappings);
    if (success) {
      setTokenMappings(updatedMappings);
      setSuccess(`Added mapping: ${normalizedAddress} → ${newMapping.coinGeckoId}`);
      
      // Reset form
      setNewMapping({ address: '', coinGeckoId: '', coinName: '' });
    }
  };

  // Remove a mapping
  const removeMapping = async (address) => {
    const updatedMappings = { ...tokenMappings };
    delete updatedMappings[address];
    
    const success = await saveMappings(updatedMappings);
    if (success) {
      setTokenMappings(updatedMappings);
      setSuccess(`Removed mapping for ${address}`);
    }
  };

  // Clear all mappings
  const clearAllMappings = async () => {
    if (window.confirm('Are you sure you want to remove all mappings?')) {
      const success = await saveMappings({});
      if (success) {
        setTokenMappings({});
        setSuccess('All mappings have been cleared');
      }
    }
  };

  // Reset to default mappings
  const resetToDefaults = async () => {
    if (window.confirm('Reset to default token mappings? This will restore the predefined token set.')) {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/reset-to-defaults`, {
          method: 'POST'
        });
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.statusText}`);
        }
        
        // Reload mappings from server
        const mappingsResponse = await fetch(`${API_URL}/mappings`);
        if (mappingsResponse.ok) {
          const data = await mappingsResponse.json();
          setTokenMappings(data);
          setSuccess('Successfully reset to default mappings');
        } else {
          throw new Error('Failed to load updated mappings');
        }
      } catch (error) {
        console.error("Error resetting to defaults:", error);
        setError(`Failed to reset to defaults: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Select a coin from the dropdown
  const selectCoin = (coin) => {
    setNewMapping({
      ...newMapping,
      coinGeckoId: coin.id,
      coinName: coin.name
    });
    setSearchTerm('');
  };

  // Copy address to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSuccess('Copied to clipboard');
        setTimeout(() => setSuccess(''), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        setError('Failed to copy to clipboard');
      });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">CoinGecko Token ID Mappings</h2>
        <p className="text-gray-600 mb-4">
          Manage the mappings between wallet addresses and CoinGecko token IDs. These mappings are used
          to fetch token prices from the CoinGecko API.
        </p>

        {/* Current Mappings Table */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Current Mappings</h3>
            <div className="space-x-2">
              <button
                onClick={resetToDefaults}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                disabled={loading}
              >
                Reset to Defaults
              </button>
              {Object.keys(tokenMappings).length > 0 && (
                <button
                  onClick={clearAllMappings}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  disabled={loading}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wallet Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CoinGecko ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(tokenMappings).map(([address, { id, name }]) => (
                  <tr key={address}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="truncate max-w-xs" title={address}>
                          {address}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(address)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Copy address"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => removeMapping(address)}
                        className="text-red-600 hover:text-red-900"
                        disabled={loading}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {Object.keys(tokenMappings).length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      No mappings found. Add your first mapping below or click "Reset to Defaults".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add New Mapping Form */}
        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">Add New Mapping</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Wallet Address
              </label>
              <input
                type="text"
                value={newMapping.address}
                onChange={(e) => setNewMapping({ ...newMapping, address: e.target.value })}
                placeholder="0x..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700">
                CoinGecko Token ID
              </label>
              <div className="mt-1 flex">
                <input
                  type="text"
                  value={newMapping.coinGeckoId}
                  onChange={(e) => setNewMapping({ 
                    ...newMapping, 
                    coinGeckoId: e.target.value,
                    coinName: e.target.value  // Default name to ID if manually typing
                  })}
                  placeholder="bitcoin"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <button
                  type="button"
                  onClick={fetchCoinList}
                  disabled={loadingCoins}
                  className="ml-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {loadingCoins ? 'Loading...' : 'Browse'}
                </button>
              </div>
            </div>
          </div>
          
          {availableCoins.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Search Coins
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, symbol, or ID..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              
              {searchTerm && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md shadow-sm">
                  {filteredCoins.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {filteredCoins.map(coin => (
                        <li 
                          key={coin.id}
                          onClick={() => selectCoin(coin)}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                        >
                          <span className="font-medium">{coin.name}</span>
                          <span className="text-gray-500">{coin.symbol} ({coin.id})</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No results found</div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="mt-4">
            <button
              type="button"
              onClick={addMapping}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Add Mapping
            </button>
          </div>
        </div>

        {/* Export/Import Buttons */}
        <div className="mt-8 flex space-x-4">
          <button
            onClick={() => {
              const dataStr = JSON.stringify(tokenMappings, null, 2);
              const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
              
              const exportFileDefaultName = 'coingecko-mappings.json';
              
              const linkElement = document.createElement('a');
              linkElement.setAttribute('href', dataUri);
              linkElement.setAttribute('download', exportFileDefaultName);
              linkElement.click();
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Export Mappings
          </button>
          
          <div className="relative">
            <button
              onClick={() => document.getElementById('import-mappings').click()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Import Mappings
            </button>
            <input
              id="import-mappings"
              type="file"
              accept=".json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = async (event) => {
                    try {
                      const importedMappings = JSON.parse(event.target.result);
                      const success = await saveMappings(importedMappings);
                      if (success) {
                        setTokenMappings(importedMappings);
                        setSuccess('Successfully imported mappings');
                      }
                    } catch (error) {
                      setError('Failed to parse the imported file');
                    }
                  };
                  reader.readAsText(file);
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoinGeckoMapper;